import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Label } from "./Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "House Painting",
  "Interior Paint",
  "Gibbing",
  "Bathroom & Home Renovations",
  "Fencing",
  "Rubbish Removal",
  "Lawn Mowing & Grounds",
  "General Maintenance",
];

const field =
  "bg-transparent border-0 border-b border-forest-ink/20 rounded-none px-0 h-12 text-forest-ink text-lg placeholder:text-forest-muted/50 focus-visible:ring-0 focus-visible:border-forest transition-colors";

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us on 027 394 3433.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <Label>Get a Quote</Label>
          <h2 className="mt-4 font-serif text-5xl md:text-7xl tracking-tighter text-forest-ink leading-[0.88]">
            Tell us about <span className="italic text-forest">the job.</span>
          </h2>
          <p className="mt-6 text-forest-muted text-base md:text-lg max-w-md leading-relaxed">
            Send a few details and Ricky will come back with a fair, honest quote.
            Prefer to talk? Call{" "}
            <a href="tel:0273943433" className="text-forest underline underline-offset-4">
              027 394 3433
            </a>
            .
          </p>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <form onSubmit={submit} data-testid="quote-form" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Input
                data-testid="quote-name"
                placeholder="Your name"
                value={form.name}
                onChange={set("name")}
                className={field}
              />
              <Input
                data-testid="quote-phone"
                placeholder="Phone number"
                value={form.phone}
                onChange={set("phone")}
                className={field}
              />
            </div>
            <Input
              data-testid="quote-email"
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={set("email")}
              className={field}
            />
            <Select
              value={form.service}
              onValueChange={(v) => setForm({ ...form, service: v })}
            >
              <SelectTrigger
                data-testid="quote-service"
                className={`${field} justify-between data-[placeholder]:text-forest-muted/50`}
              >
                <SelectValue placeholder="What do you need done?" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`service-opt-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              data-testid="quote-message"
              placeholder="Tell us a little about the property and the work..."
              value={form.message}
              onChange={set("message")}
              rows={4}
              className="bg-transparent border-0 border-b border-forest-ink/20 rounded-none px-0 text-forest-ink text-lg placeholder:text-forest-muted/50 focus-visible:ring-0 focus-visible:border-forest resize-none transition-colors"
            />
            <button
              type="submit"
              data-testid="quote-submit"
              disabled={loading}
              className="group inline-flex items-center gap-3 rounded-full bg-forest text-bone pl-7 pr-2 py-2.5 text-sm tracking-wide disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send request"}
              <span className="grid place-items-center h-9 w-9 rounded-full bg-bone text-forest group-hover:translate-x-1 transition-transform duration-300">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              </span>
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
